import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';
import { redactUrl } from '../util/redact.util';
import { isBackendUrl, toBackendPath } from './backend-request.util';

const maxDetailsLength = 300;
const errorBodyFields = ['title', 'detail', 'message', 'errorKey', 'error'];

/**
 * Logs one compact line per backend request, e.g. `http GET /api/challenges/4081 200 84ms`, plus the details of the
 * response body for failed requests. Console output is forwarded to Bugfender, so urls are redacted and neither
 * headers nor request bodies are logged. Third party requests are skipped, their urls may carry their own API keys, and
 * so are the app's own assets (e.g. translations).
 */
@Injectable()
export class HttpLoggingInterceptor implements HttpInterceptor {
  public intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!request.url || !isBackendUrl(request.url)) {
      return next.handle(request);
    }

    const label = `http ${request.method} ${redactUrl(toBackendPath(request.urlWithParams))}`;
    const startedAt = Date.now();
    const elapsed = (): string => `${Date.now() - startedAt}ms`;
    let completed = false;

    return next.handle(request).pipe(
      tap({
        next: event => {
          if (event instanceof HttpResponse) {
            completed = true;
            console.log(`${label} ${event.status} ${elapsed()}`);
          }
        },
        error: (error: unknown) => {
          completed = true;
          logFailure(label, error, elapsed());
        },
      }),
      finalize(() => {
        // unsubscribed before a response arrived, e.g. by a switchMap
        if (!completed) {
          console.log(`${label} cancelled after ${elapsed()}`);
        }
      })
    );
  }
}

function logFailure(label: string, error: unknown, elapsed: string): void {
  if (!(error instanceof HttpErrorResponse)) {
    console.error(`${label} failed after ${elapsed}`, truncate(String(error)));
    return;
  }
  if (error.status === 0) {
    console.error(`${label} 0 network error ${elapsed}`);
    return;
  }
  const details = describeErrorBody(error.error);
  const message = `${label} ${error.status} ${elapsed}${details ? ' ' + details : ''}`;
  if (error.status < 500) {
    console.warn(message);
  } else {
    console.error(message);
  }
}

/**
 * Picks the interesting fields of an error body - a full problem JSON or proxy error page would bloat the log.
 */
export function describeErrorBody(body: unknown): string | undefined {
  if (typeof body === 'string') {
    const text = body.replace(/\s+/g, ' ').trim();
    return text ? truncate(text) : undefined;
  }
  if (!body || typeof body !== 'object' || isBinaryOrEvent(body)) {
    return undefined;
  }
  const record = body as Record<string, unknown>;
  const fields = Object.fromEntries(
    errorBodyFields.filter(field => record[field] !== undefined).map(field => [field, record[field]])
  );
  return truncate(JSON.stringify(Object.keys(fields).length > 0 ? fields : body));
}

function isBinaryOrEvent(body: object): boolean {
  return (
    (typeof ProgressEvent !== 'undefined' && body instanceof ProgressEvent) ||
    (typeof Blob !== 'undefined' && body instanceof Blob) ||
    body instanceof ArrayBuffer
  );
}

function truncate(text: string): string {
  return text.length > maxDetailsLength ? text.slice(0, maxDetailsLength) + '…' : text;
}
